import { useEffect, useState } from "react";
import { PlusIcon, XIcon } from "lucide-react";

import "./LiveChatSection.scss";
import Message from "../../../../i18n/Message";
import Notify from "../../../shared/Notify";
import backend from "../../../../backend/backend";
import { useAutosave, AutosaveIndicator } from "../../../shared/autosave";

const parsePrompts = (json) => {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
};

// Owner-facing editor for the Live Chat intro message + featured prompts. Persists to
// the playground (liveChatIntro / liveChatPromptsJson) via debounced autosave. Lives in
// the details-page Settings tab; the widget's Live Chat tab now always shows the runtime.
//
// `persist(fields)` lets a host (the Settings tab) merge these fields onto the freshest
// playground snapshot so concurrent autosaves from sibling tiles don't clobber each other.
// When omitted, it falls back to a standalone full-object save. `onChange(fields)` (optional)
// is fired at edit time so the host can record the edit before the debounced save fires.
// `onStatus(status)` (optional) lets the host render the autosave indicator itself (e.g. in
// the tile header); when provided, this component hides its own bottom indicator.
const LiveChatManageEditor = ({ playground, persist, onChange, onStatus }) => {
  const [intro, setIntro] = useState(playground.liveChatIntro || "");
  const [prompts, setPrompts] = useState(
    parsePrompts(playground.liveChatPromptsJson)
  );

  const fieldsFor = (nextIntro, nextPrompts) => ({
    liveChatIntro: nextIntro,
    liveChatPromptsJson: JSON.stringify(
      nextPrompts.map((p) => p.trim()).filter(Boolean)
    ),
  });

  const { status, schedule } = useAutosave(async () => {
    const fields = fieldsFor(intro, prompts);
    try {
      if (persist) {
        await persist(fields);
      } else {
        await backend.savePlayground({ ...playground, ...fields });
      }
    } catch (e) {
      Notify.error(<Message id="widget_live_chat_save_failed" />);
      throw e;
    }
  });

  // Surface the autosave status to the host so it can render the indicator in the tile
  // header (unified with the sibling tile) instead of at the bottom of this editor.
  useEffect(() => {
    if (onStatus) onStatus(status);
  }, [status, onStatus]);

  const updateIntro = (value) => {
    setIntro(value);
    if (onChange) onChange(fieldsFor(value, prompts));
    schedule();
  };

  const updatePrompts = (next) => {
    setPrompts(next);
    if (onChange) onChange(fieldsFor(intro, next));
    schedule();
  };

  return (
    <div className="live_chat_manage">
      <label className="field_label">
        <Message id="widget_live_chat_intro_label" />
      </label>
      <textarea
        className="intro_editor"
        rows={2}
        value={intro}
        onChange={(e) => updateIntro(e.target.value)}
      />
      <label className="field_label">
        <Message id="widget_live_chat_prompts_label" />
      </label>
      {prompts.map((p, i) => (
        <div className="prompt_editor_row" key={i}>
          <input
            value={p}
            onChange={(e) => {
              const next = [...prompts];
              next[i] = e.target.value;
              updatePrompts(next);
            }}
          />
          <button
            type="button"
            className="icon_btn"
            onClick={() => updatePrompts(prompts.filter((_, idx) => idx !== i))}
          >
            <XIcon size={16} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn btn-outline-dark btn-sm add_prompt"
        // Appending an empty prompt has nothing to persist yet (blank prompts are
        // filtered out on save), so don't schedule a save until the user types.
        onClick={() => setPrompts([...prompts, ""])}
      >
        <PlusIcon size={16} /> <Message id="widget_live_chat_add_prompt" />
      </button>
      {!onStatus && (
        <div className="manage_actions">
          <AutosaveIndicator status={status} />
        </div>
      )}
    </div>
  );
};

export default LiveChatManageEditor;
